<template>
  <div :class="cls.header" :style="headerStyle">
    <div :class="cls.titleWrap" :style="titleWrapStyle">
      <!-- underline 模式 -->
      <JdTooltip v-if="isUnderlineMode" effect="light" v-bind="header.tooltipProps" :content="tooltipText">
        <div :class="cls.title" :style="underlineTitleStyle">
          {{ header.title }}
        </div>
        <template v-if="isTooltipVNode" #content>
          <component :is="TooltipVNodeHost" />
        </template>
      </JdTooltip>
      <!-- question 模式 -->
      <EllipsisText v-else :class="cls.title" :style="baseTitleStyle" :title="String(header.title ?? '')">
        {{ header.title }}
      </EllipsisText>

      <!-- 问号图标 -->
      <JdTooltip v-if="effectiveTooltipMode === 'question' && header.tooltip" effect="light" v-bind="header.tooltipProps" :content="tooltipText">
        <QuestionCircle :class="cls.tooltipTrigger" :style="questionIconStyle" />
        <template v-if="isTooltipVNode" #content>
          <component :is="TooltipVNodeHost" />
        </template>
      </JdTooltip>

      <!-- titleExtra -->
      <span v-if="header.titleExtra" :class="cls.titleExtra" :style="{ flexShrink: 0 }">
        <component :is="header.titleExtra" v-if="isVNodeLike(header.titleExtra)" />
        <template v-else>{{ header.titleExtra }}</template>
      </span>
    </div>

    <!-- headerExtra -->
    <div v-if="header.extra" :class="cls.headerExtra" :style="{ flexShrink: 0 }">
      <component :is="header.extra" v-if="isVNodeLike(header.extra)" />
      <template v-else>{{ header.extra }}</template>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  markRaw,
  type CSSProperties,
  type PropType,
  type Component,
} from 'vue'
import { useNamespace } from '@jdesign-vue/hooks'
import JdTooltip from '../../../tooltip'
import { QuestionCircle } from '@jd/jdesign-icons-vue'
import EllipsisText from '../supporting/ellipsis-text.vue'
import type { StatisticCardResolvedHeader, StatisticCardSize } from '../types'
import { createColorVars, STATISTIC_CARD_CLASSES } from '../constants'

export default defineComponent({
  name: 'StatisticCardTitle',
  components: { JdTooltip, QuestionCircle, EllipsisText },
  props: {
    header: { type: Object as PropType<StatisticCardResolvedHeader>, required: true },
    size: { type: String as PropType<StatisticCardSize>, required: true },
  },
  setup(props) {
    const cls = STATISTIC_CARD_CLASSES
    const ns = useNamespace('statistic-card')
    const colorVars = createColorVars(ns.cssVarName)

    const effectiveTooltipMode = computed(() =>
      props.header.tooltip ? (props.header.tooltipMode ?? 'question') : undefined,
    )
    const isUnderlineMode = computed(() => effectiveTooltipMode.value === 'underline')

    // tooltip 支持纯文本与 VNode 两种内容:
    // 纯文本走 JdTooltip 的 `content` 属性，VNode 走 `#content` 插槽，
    // 否则 VNode 对象会被 Tooltip 当作字符串渲染成 JSON。
    const isTooltipVNode = computed(
      () => !!props.header.tooltip && typeof props.header.tooltip === 'object',
    )
    const tooltipText = computed(() => {
      const t = props.header.tooltip
      return t == null || typeof t === 'object' ? undefined : String(t)
    })
    // 显式标注为 `Component`：markRaw 的返回类型带有 Vue 内部未导出的 RawSymbol 标记，
    // 若让 TS 自行推断，会随 setup 返回值泄漏到组件公开类型，导致 dts 生成报 TS4082。
    const TooltipVNodeHost: Component = markRaw({
      render: () => props.header.tooltip,
    })

    const baseTitleStyle = computed<CSSProperties>(() => ({
      fontSize: props.size === 'large' ? '14px' : '12px',
      fontWeight: 600,
      lineHeight: props.size === 'large' ? '22px' : '18px',
      color: colorVars.textLabel,
    }))

    const underlineTitleStyle = computed<CSSProperties>(() => ({
      ...baseTitleStyle.value,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      borderBottom: `1px dashed ${colorVars.textLabel}`,
      cursor: 'pointer',
    }))

    const headerStyle: CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
    }

    const titleWrapStyle: CSSProperties = {
      minWidth: 0,
      flex: 1,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    }

    const questionIconStyle = computed<CSSProperties>(() => ({
      color: colorVars.textSecondary,
      cursor: 'pointer',
      flexShrink: 0,
      fontSize: props.size === 'large' ? '14px' : '12px',
      width: props.size === 'large' ? '14px' : '12px',
      height: props.size === 'large' ? '14px' : '12px',
    }))

    function isVNodeLike(v: unknown): boolean {
      return !!v && typeof v === 'object' && (v as any).__v_isVNode === true
    }

    return {
      cls,
      effectiveTooltipMode,
      isUnderlineMode,
      isTooltipVNode,
      tooltipText,
      TooltipVNodeHost,
      baseTitleStyle,
      underlineTitleStyle,
      headerStyle,
      titleWrapStyle,
      questionIconStyle,
      isVNodeLike,
    }
  },
})
</script>